import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';

function LegalShell({ title, updated, children }) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F1E8]">
      <header className="border-b border-[#0B3B2E]/10">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-9 h-9 rounded-full bg-[#0B3B2E] flex items-center justify-center group-hover:rotate-45 transition-transform duration-700">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M4 20 L12 4 L20 20" stroke="#C8944B" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="20" r="1.6" fill="#F5F1E8" />
              </svg>
            </div>
            <span className="font-serif text-[22px] font-semibold text-[#0B3B2E] tracking-tight">
              Vector<span className="text-[#C8944B]">FX</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[13px] text-[#0B3B2E] hover:text-[#12503F] font-medium"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-[820px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#0B3B2E]/70 font-medium">Legal</p>
        <h1 className="mt-3 font-serif text-[40px] lg:text-[56px] leading-[1.05] text-[#0B3B2E]">{title}</h1>
        <p className="mt-4 text-[13px] text-[#0E1F1A]/55">Last updated: {updated}</p>

        <article className="mt-10 space-y-6 text-[15px] leading-relaxed text-[#0E1F1A]/80">{children}</article>

        <div className="mt-16 pt-8 border-t border-[#0B3B2E]/10 text-[12px] text-[#0E1F1A]/55">
          Have a question about this policy? Email{' '}
          <a href="mailto:legal@vectorfx.co.uk" className="underline text-[#0B3B2E]">legal@vectorfx.co.uk</a>.
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-serif text-[22px] lg:text-[26px] text-[#0B3B2E] mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

export function Terms() {
  return (
    <LegalShell title="Terms of use" updated="August 2025">
      <p>
        These Terms of Use govern your access to and use of the Vector FX website and the online
        services made available through it. By using the site you agree to be bound by these Terms.
        If you do not agree, please do not use the site.
      </p>

      <Section title="1. Who we are">
        <p>
          Vector FX Ltd is a company registered in England and Wales (company no. 12345678) with a
          registered office at 12 New Fetter Lane, London EC4A 1JP. In these Terms, “Vector FX”,
          “we”, “us” and “our” refer to Vector FX Ltd.
        </p>
      </Section>

      <Section title="2. Eligibility">
        <p>
          You must be at least 18 years old to open an account or use our services. By registering
          you confirm that the information you provide is accurate and complete.
        </p>
      </Section>

      <Section title="3. Your account">
        <p>
          You are responsible for keeping your account credentials safe. Do not share your password
          and notify us immediately if you suspect unauthorised access.
        </p>
      </Section>

      <Section title="4. Acceptable use">
        <p>
          You agree not to use the site or services for anything unlawful, to interfere with the
          site’s operation, or to attempt to gain unauthorised access to any part of it.
        </p>
      </Section>

      <Section title="5. Content and intellectual property">
        <p>
          All content on this site, including text, logos, graphics and software, is owned by
          Vector FX or its licensors and is protected by intellectual property laws. You may not
          copy, modify or distribute any content without prior written permission.
        </p>
      </Section>

      <Section title="6. Availability">
        <p>
          We work hard to keep the site available but do not guarantee uninterrupted access. We may
          suspend, withdraw or restrict availability of all or part of the site for maintenance,
          security, legal or business reasons.
        </p>
      </Section>

      <Section title="7. Limitation of liability">
        <p>
          To the extent permitted by law, Vector FX excludes all implied conditions, warranties and
          representations. Nothing in these Terms limits liability that cannot be excluded under
          applicable law (for example, liability for death or personal injury caused by negligence,
          or for fraud).
        </p>
      </Section>

      <Section title="8. Changes to these Terms">
        <p>
          We may update these Terms from time to time. The latest version will always be available
          on this page with the date of the most recent update.
        </p>
      </Section>

      <Section title="9. Governing law">
        <p>
          These Terms are governed by the laws of England and Wales, and any dispute will be
          subject to the exclusive jurisdiction of the English courts.
        </p>
      </Section>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Privacy notice" updated="August 2025">
      <p>
        This Privacy Notice explains how Vector FX Ltd collects, uses and protects your personal
        information when you visit our website or use our services. We are the data controller
        under the UK GDPR and the Data Protection Act 2018.
      </p>

      <Section title="1. Information we collect">
        <p>
          We collect the information you give us directly (such as your name, email address, phone
          number and account details), information collected automatically when you use the site
          (such as device, browser and usage data), and information from third parties where you
          have permitted them to share it with us.
        </p>
      </Section>

      <Section title="2. How we use your information">
        <p>
          We use your information to provide and improve our services, to communicate with you, to
          keep our platform secure, to meet our regulatory obligations (including anti-money
          laundering checks) and, where you have opted in, to send you product updates and
          insights.
        </p>
      </Section>

      <Section title="3. Lawful bases">
        <p>
          We process your data on the following bases: to perform a contract with you; to comply
          with a legal obligation; where you have given us consent; and for our legitimate
          interests in running and improving our business.
        </p>
      </Section>

      <Section title="4. Sharing">
        <p>
          We share limited data with trusted service providers who help us operate the platform
          (for example, hosting, analytics and identity verification). We do not sell your personal
          data.
        </p>
      </Section>

      <Section title="5. International transfers">
        <p>
          Some of our providers are based outside the UK. When we transfer data internationally we
          use appropriate safeguards such as the UK International Data Transfer Agreement or
          Standard Contractual Clauses.
        </p>
      </Section>

      <Section title="6. Retention">
        <p>
          We keep your personal data only for as long as we need it to provide our services and to
          meet legal and regulatory obligations, then we securely delete or anonymise it.
        </p>
      </Section>

      <Section title="7. Your rights">
        <p>
          You have the right to access, correct, delete or restrict the use of your personal data,
          to object to processing, and to data portability. You can exercise these rights by
          emailing privacy@vectorfx.co.uk. You also have the right to complain to the UK Information
          Commissioner’s Office (ico.org.uk).
        </p>
      </Section>

      <Section title="8. Security">
        <p>
          We use technical and organisational measures, including encryption in transit and at
          rest, to protect your information. No system is ever completely secure, but we work hard
          to keep yours safe.
        </p>
      </Section>

      <Section title="9. Contact us">
        <p>
          Questions about this notice? Write to us at Vector FX Ltd, 12 New Fetter Lane, London
          EC4A 1JP, or email privacy@vectorfx.co.uk.
        </p>
      </Section>
    </LegalShell>
  );
}

export function Cookies() {
  return (
    <LegalShell title="Cookie policy" updated="August 2025">
      <p>
        Vector FX uses cookies and similar technologies to help the site work, to understand how it
        is used, and to improve your experience. This policy explains what cookies we use and how
        you can control them.
      </p>

      <Section title="1. What are cookies?">
        <p>
          Cookies are small text files placed on your device by websites that you visit. They let
          the site remember your actions and preferences over a period of time.
        </p>
      </Section>

      <Section title="2. Cookies we use">
        <p>
          <strong>Essential cookies</strong> keep the site secure and remember your session so you
          stay signed in. These are always on. <strong>Analytics cookies</strong> help us
          understand how visitors use the site so we can improve it. <strong>Preference
          cookies</strong> remember choices you make, such as your language.
        </p>
      </Section>

      <Section title="3. Your choices">
        <p>
          When you first visit the site we ask for your consent to non-essential cookies. You can
          change your choice at any time by clearing the site’s data in your browser or by
          contacting us. Most browsers also let you block or delete cookies through their settings.
        </p>
      </Section>

      <Section title="4. Third parties">
        <p>
          Some cookies are set by third-party services we use (for example, analytics providers).
          Those third parties have their own privacy and cookie policies.
        </p>
      </Section>

      <Section title="5. Contact">
        <p>
          Questions about our use of cookies? Email privacy@vectorfx.co.uk.
        </p>
      </Section>
    </LegalShell>
  );
}

export function ModernSlavery() {
  return (
    <LegalShell title="Modern slavery & human trafficking statement" updated="February 2026">
      <p data-testid="modern-slavery-intro">
        This statement is published pursuant to section 54(1) of the UK Modern Slavery Act 2015. It
        sets out the steps Vector FX Ltd (&ldquo;Vector FX&rdquo;, &ldquo;we&rdquo;) has taken during
        the current financial year to prevent modern slavery and human trafficking from taking place
        in our business and in our supply chains.
      </p>

      <Section title="1. Our organisation">
        <p>
          Vector FX Ltd is a UK-authorised payments and foreign-exchange business, regulated by the
          Financial Conduct Authority (FRN 900000). We offer international payments, multi-currency
          accounts, property FX (Vector Settle &amp; Vector Bridge), bulk payments, forward contracts
          and global payroll to individuals and businesses across the UK and EEA.
        </p>
      </Section>

      <Section title="2. Our commitment">
        <p>
          Vector FX has a zero-tolerance approach to modern slavery, forced labour, servitude and
          human trafficking. We are committed to acting ethically and with integrity in all our
          business relationships, and to implementing and enforcing effective systems and controls
          to make sure modern slavery is not taking place anywhere in our own operations or in any
          part of our supply chains.
        </p>
      </Section>

      <Section title="3. Our supply chains">
        <p>
          Our supply chains are typically low-risk for modern slavery and consist of professional
          service providers: banks and correspondent institutions, technology and cloud vendors,
          compliance and legal advisors, and marketing partners &mdash; almost all of which are
          headquartered in the UK, EEA or United States.
        </p>
      </Section>

      <Section title="4. Due diligence and risk assessment">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            All new suppliers are onboarded through our supplier due-diligence programme, which
            includes an assessment of modern-slavery risk based on jurisdiction, sector and headcount.
          </li>
          <li>
            Higher-risk suppliers (for example, hardware manufacturers or facilities contractors) are
            required to complete a supplier ethics questionnaire and to confirm compliance with the
            Modern Slavery Act 2015.
          </li>
          <li>
            Ongoing monitoring is embedded in our compliance calendar and reviewed by the Money
            Laundering Reporting Officer (MLRO) annually.
          </li>
        </ul>
      </Section>

      <Section title="5. Policies">
        <p>
          Our anti-slavery and human-trafficking policy sits alongside our AML, whistle-blowing,
          recruitment, and code-of-conduct policies. All employees receive training on financial
          crime, ethics and safeguarding at induction and annually thereafter. Our whistle-blowing
          channel allows anyone (including contractors and suppliers) to raise concerns anonymously.
        </p>
      </Section>

      <Section title="6. Training">
        <p>
          All staff complete mandatory training on financial-crime prevention which includes a module
          on identifying signs of human trafficking and modern slavery in financial transactions and
          customer interactions. Compliance and operations staff receive additional deep-dive
          training.
        </p>
      </Section>

      <Section title="7. Effectiveness">
        <p>
          We measure the effectiveness of these steps through supplier audit completion rates,
          training completion rates, the number of whistle-blowing reports received and reviewed, and
          the number of modern-slavery referrals made (if any) to the UK National Referral Mechanism.
          To date, no reports of modern slavery have been made in relation to Vector FX or our supply
          chain.
        </p>
      </Section>

      <Section title="8. Governance and approval">
        <p>
          This statement was approved by the board of directors of Vector FX Ltd and is signed on
          behalf of the board by the Chief Executive Officer. It will be reviewed and updated
          annually.
        </p>
        <p className="mt-4 text-[13px] text-[#0E1F1A]/60">
          Signed: On behalf of the board of Vector FX Ltd &middot; February 2026
        </p>
      </Section>

      <Section title="9. Contact">
        <p>
          To raise a modern-slavery concern or request further information about our controls,
          please write to <a className="underline text-[#0B3B2E]" href="mailto:compliance@vectorfx.co.uk">compliance@vectorfx.co.uk</a>{' '}
          or use our confidential whistle-blowing channel described in our Code of Conduct.
        </p>
      </Section>
    </LegalShell>
  );
}

